import { useState, useMemo, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  HStack,
  Divider,
  useToast,
} from '@chakra-ui/react';

import type { APIRemark, APIRemarkFile, APIAppeal } from 'types/api.types';
import {
  ValidationColumn,
  RemarksColumn,
  FileUploadsColumn,
  CommentsSection,
  SupportingFilesSection,
  SubmissionStatusAlerts,
} from './components';
import {
  getRemarkId,
  getFileId,
  getChildRemarks,
  getChildFiles,
} from './helpers';
import { useAppealPolling, useAppealFiles } from './hooks';
import isDeferralAllowed from 'helpers/isDeferralAllowed';
import { useFeatureFlag } from 'hooks/useFeatureFlag';

interface ReferToUnderwritingModalProps {
  isOpen: boolean;
  onClose: () => void;
  validations: Validation[];
  remarks: APIRemark[];
  files: APIRemarkFile[];
  uuid: string;
  onSubmit: (selectedItems: {
    validations: string[];
    remarks: string[];
    files: string[];
    comments: string;
    supporting_files?: number[];
  }) => Promise<{ appeal_log_id: number }>;
  onSuccess?: () => void;
  isSubmitting?: boolean;
  appeal?: APIAppeal | null;
  mandatoryFileValidations: string[];
}

const ReferToUnderwritingModal = ({
  isOpen,
  onClose,
  validations,
  remarks,
  files,
  uuid,
  onSubmit,
  onSuccess,
  isSubmitting = false,
  appeal,
  mandatoryFileValidations,
}: ReferToUnderwritingModalProps) => {
  const [selectedValidations, setSelectedValidations] = useState<string[]>([]);
  const [selectedRemarks, setSelectedRemarks] = useState<string[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [comments, setComments] = useState('');
  const [hasLoadedFromHistory, setHasLoadedFromHistory] = useState(false);
  const toast = useToast();
  const isOwnershipOnlyDeferralEnabled = useFeatureFlag(
    'enable-ownership-only-deferral',
  );

  // Use custom hooks for file management and polling
  const {
    supportingFiles,
    isUploadingFile,
    deletingFileId,
    handleFilesUpload,
    handleFileRemove,
    resetFiles,
  } = useAppealFiles(uuid, isOpen, appeal?.supporting_files);

  const {
    submissionStatus,
    setSubmissionStatus,
    errorMessage,
    startPolling,
    stopPolling,
    resetPollingState,
  } = useAppealPolling(uuid);

  const problemValidations = useMemo(() => {
    return validations.filter(
      (v) =>
        v.validation_outcome === 'caution' || v.validation_outcome === 'fail',
    );
  }, [validations]);

  const problemRemarks = useMemo(() => {
    return remarks.filter((r) => r.status !== 'approved');
  }, [remarks]);

  const problemFiles = useMemo(() => {
    return files
      .map((f) => ({
        ...f,
        mandatory:
          mandatoryFileValidations.includes(f.validation_key) ||
          !isDeferralAllowed(
            f.validation_key,
            validations,
            isOwnershipOnlyDeferralEnabled,
          ),
      }))
      .filter((f) => f.status !== 'approved' && f.mandatory);
  }, [
    files,
    mandatoryFileValidations,
    validations,
    isOwnershipOnlyDeferralEnabled,
  ]);

  // Initialize from referral history prop or default to all selected when modal opens
  useEffect(() => {
    if (isOpen && !hasLoadedFromHistory) {
      if (appeal) {
        // Pre-populate with data from last referral
        setSelectedValidations(appeal.validations || []);
        setSelectedRemarks(appeal.remarks || []);
        setSelectedFiles(appeal.files || []);
        setComments(appeal.comments || '');
      } else {
        // No history - default to all problem items selected
        setSelectedValidations(problemValidations.map((v) => v.validation_id));
        setSelectedRemarks(
          problemRemarks.map((r) => `${r.validation_key}-${r.item_key}`),
        );
        setSelectedFiles(
          problemFiles.map((f) => `${f.validation_key}-${f.item_key}`),
        );
        setComments('');
      }
      setHasLoadedFromHistory(true);
    }
  }, [
    isOpen,
    hasLoadedFromHistory,
    appeal,
    problemValidations,
    problemRemarks,
    problemFiles,
  ]);

  // Auto-close modal on successful submission
  useEffect(() => {
    if (submissionStatus === 'success') {
      // Update wip_status to lock the form
      if (onSuccess) {
        onSuccess();
      }
      handleClose();
    }
  }, [submissionStatus, onSuccess]);

  const handleValidationToggle = (id: string) => {
    setSelectedValidations((prev) => {
      const isCurrentlySelected = prev.includes(id);

      if (isCurrentlySelected) {
        // Unchecking validation - remove it and uncheck child remarks/files
        const childRemarks = getChildRemarks(id, problemRemarks);
        const childFiles = getChildFiles(id, problemFiles);

        setSelectedRemarks((prevRemarks) =>
          prevRemarks.filter(
            (remarkId) =>
              !childRemarks.some((r) => getRemarkId(r) === remarkId),
          ),
        );

        setSelectedFiles((prevFiles) =>
          prevFiles.filter(
            (fileId) => !childFiles.some((f) => getFileId(f) === fileId),
          ),
        );

        return prev.filter((v) => v !== id);
      } else {
        // Checking validation - add it and auto-check child remarks/files
        const childRemarks = getChildRemarks(id, problemRemarks);
        const childFiles = getChildFiles(id, problemFiles);

        setSelectedRemarks((prevRemarks) => {
          const newRemarkIds = childRemarks.map((r) => getRemarkId(r));
          return [...new Set([...prevRemarks, ...newRemarkIds])];
        });

        setSelectedFiles((prevFiles) => {
          const newFileIds = childFiles.map((f) => getFileId(f));
          return [...new Set([...prevFiles, ...newFileIds])];
        });

        return [...prev, id];
      }
    });
  };

  const handleRemarkToggle = (id: string) => {
    setSelectedRemarks((prev) =>
      prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id],
    );
  };

  const handleFileToggle = (id: string) => {
    setSelectedFiles((prev) =>
      prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id],
    );
  };

  const handleSubmit = async () => {
    setSubmissionStatus('submitting');
    try {
      const response = await onSubmit({
        validations: selectedValidations,
        remarks: selectedRemarks,
        files: selectedFiles,
        comments,
        supporting_files:
          supportingFiles.length > 0
            ? supportingFiles.map((f) => f.id)
            : undefined,
      });

      // Start polling if we got an appeal_log_id
      if (response?.appeal_log_id) {
        startPolling(response.appeal_log_id);
      } else {
        setSubmissionStatus('error');
        toast({
          title: 'Submission error',
          description: 'Failed to get appeal ID',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error: any) {
      setSubmissionStatus('error');
      toast({
        title: 'Submission failed',
        description: error?.message || 'Failed to submit referral',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleClose = () => {
    stopPolling();
    resetPollingState();
    resetFiles();
    setSelectedValidations([]);
    setSelectedRemarks([]);
    setSelectedFiles([]);
    setComments('');
    setHasLoadedFromHistory(false);
    onClose();
  };

  const isSubmitDisabled = !comments.trim() || submissionStatus !== 'idle';

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="6xl"
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent maxW={{ base: '95vw', md: '90vw', lg: '1200px' }}>
        <ModalHeader>Refer to Underwriting</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <SubmissionStatusAlerts
            status={submissionStatus}
            errorMessage={errorMessage}
          />

          <HStack
            align="flex-start"
            spacing={{ base: 0, lg: 4 }}
            mb={6}
            flexDirection={{ base: 'column', lg: 'row' }}
          >
            <ValidationColumn
              validations={problemValidations}
              selectedValidations={selectedValidations}
              onToggle={handleValidationToggle}
            />

            <Divider
              orientation="vertical"
              h="300px"
              display={{ base: 'none', lg: 'block' }}
            />

            <RemarksColumn
              remarks={problemRemarks}
              validations={validations}
              selectedRemarks={selectedRemarks}
              selectedValidations={selectedValidations}
              onToggle={handleRemarkToggle}
            />

            <Divider
              orientation="vertical"
              h="300px"
              display={{ base: 'none', lg: 'block' }}
            />

            <FileUploadsColumn
              files={problemFiles}
              validations={validations}
              selectedFiles={selectedFiles}
              selectedValidations={selectedValidations}
              onToggle={handleFileToggle}
            />
          </HStack>

          <Divider my={4} />

          <CommentsSection comments={comments} onChange={setComments} />

          <Divider my={4} />

          <SupportingFilesSection
            uploadedFiles={supportingFiles}
            onFilesUpload={handleFilesUpload}
            onFileRemove={handleFileRemove}
            isUploading={isUploadingFile}
            isDeletingFileId={deletingFileId}
          />
        </ModalBody>

        <ModalFooter flexDirection={{ base: 'column', sm: 'row' }} gap={2}>
          <Button
            variant="ghost"
            onClick={handleClose}
            w={{ base: '100%', sm: 'auto' }}
            isDisabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            colorScheme="brand"
            onClick={handleSubmit}
            isDisabled={isSubmitDisabled || isSubmitting}
            isLoading={
              submissionStatus === 'submitting' ||
              submissionStatus === 'polling' ||
              isSubmitting
            }
            loadingText={
              submissionStatus === 'submitting'
                ? 'Submitting...'
                : submissionStatus === 'polling'
                  ? 'Creating ticket...'
                  : 'Submitting...'
            }
            w={{ base: '100%', sm: 'auto' }}
          >
            {submissionStatus === 'success' ? 'Submitted' : 'Submit Referral'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ReferToUnderwritingModal;
